/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useMemo } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import DiagramArchitectureMapIcon from '@atlaskit/icon-lab/core/diagram-architecture-map';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import ListIcon from '@atlaskit/icon/core/list-checklist';
import TableIcon from '@atlaskit/icon/core/table';
import { Text } from '@atlaskit/primitives/compiled';

import { messages } from '../../common/messages';

import type { ManagedBlockSourceMode } from '../../common/agentManagedExtensionPluginType';
import type { ManagedBlockGoal, ManagedBlockOutputFormat } from './configuration';

export type SelectOption<T extends string> = {
	label: string;
	value: T;
};

export type ContentOptionValue = ManagedBlockGoal | 'prompt';

export type FormatOption = SelectOption<ManagedBlockOutputFormat> & {
	icon: JSX.Element;
	isLocked?: boolean;
};

export type ConfigPanelOptions = {
	goalOptions: SelectOption<ContentOptionValue>[];
	outputFormatOptions: FormatOption[];
	sourceModeOptions: SelectOption<ManagedBlockSourceMode>[];
	systemDesignOutputFormatOptions: FormatOption[];
};

export const getSelectedOption = <T extends string>(
	options: SelectOption<T>[],
	value: T,
): SelectOption<T> | undefined => options.find((option) => option.value === value);

export const useConfigPanelOptions = (): ConfigPanelOptions => {
	const intl = useIntl();

	return useMemo(
		() => ({
			goalOptions: [
				{
					label: intl.formatMessage(messages.goalExecutiveSummary),
					value: 'executive-summary' as const,
				},
				{
					label: intl.formatMessage(messages.goalDecisionRegister),
					value: 'decision-register' as const,
				},
				{
					label: intl.formatMessage(messages.goalMeetingRecap),
					value: 'meeting-recap' as const,
				},
				// Keep System design ready for later, but do not show it in the goal dropdown yet.
				// {
				// 	label: intl.formatMessage(messages.goalSystemDesign),
				// 	value: 'system-design' as const,
				// },
				{
					label: intl.formatMessage(messages.promptToggleLabel),
					value: 'prompt' as const,
				},
			] satisfies SelectOption<ContentOptionValue>[],
			outputFormatOptions: [
				{
					icon: (
						<Text as="span" size="large" aria-hidden="true">
							{intl.formatMessage(messages.outputFormatTextIcon)}
						</Text>
					),
					label: intl.formatMessage(messages.outputFormatText),
					value: 'text' as const,
				},
				{
					icon: <ListIcon label="" color="currentColor" />,
					label: intl.formatMessage(messages.outputFormatList),
					value: 'list' as const,
				},
				{
					icon: <TableIcon label="" color="currentColor" />,
					label: intl.formatMessage(messages.outputFormatTable),
					value: 'table' as const,
				},
				{
					icon: <InformationCircleIcon label="" color="currentColor" />,
					label: intl.formatMessage(messages.outputFormatPanel),
					value: 'panel' as const,
				},
			] satisfies FormatOption[],
			sourceModeOptions: [
				{
					label: intl.formatMessage(messages.sourcesModeManual),
					value: 'manual' as const,
				},
				{
					label: intl.formatMessage(messages.sourcesModeCurrentPage),
					value: 'current-page' as const,
				},
			] satisfies SelectOption<ManagedBlockSourceMode>[],
			systemDesignOutputFormatOptions: [
				{
					icon: <DiagramArchitectureMapIcon label="" color="currentColor" />,
					isLocked: true,
					label: intl.formatMessage(messages.outputFormatDiagram),
					value: 'diagram' as const,
				},
			] satisfies FormatOption[],
		}),
		[intl],
	);
};
